import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMercadoPago } from '../hooks/useMercadoPago';
import { buildApiUrl } from '../services/api';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import {
  AppliedCoupon,
  createEmptyPaymentFormData,
  PaymentFormData,
  SubscriptionPlan,
  SubscriptionPlanId
} from '../types/payment';
import {
  getCouponDiscountPercent,
  getCouponFreeDays,
  getCouponSummaryText,
  isFreeDaysCoupon,
  normalizeCouponCode,
  parseCouponValidationResponse
} from '../utils/couponUtils';
import { maskCPF } from '../utils/documentUtils';
import { normalizePhoneInput, validatePhone } from '../utils/phoneUtils';

const isValidCPF = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');

  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const numbers = digits.split('').map(Number);

  const firstSum = numbers
    .slice(0, 9)
    .reduce((sum, num, index) => sum + num * (10 - index), 0);
  let firstCheck = (firstSum * 10) % 11;
  if (firstCheck === 10) firstCheck = 0;
  if (firstCheck !== numbers[9]) return false;

  const secondSum = numbers
    .slice(0, 10)
    .reduce((sum, num, index) => sum + num * (11 - index), 0);
  let secondCheck = (secondSum * 10) % 11;
  if (secondCheck === 10) secondCheck = 0;

  return secondCheck === numbers[10];
};

const registerSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, 'Telefone inválido'),
  document: z.string().min(11, 'CPF é obrigatório').refine(isValidCPF, 'CPF inválido'),
  registroNumero: z.string().min(2, 'Número de registro é obrigatório'),
  orgaoEmissor: z.string().min(2, 'Órgão emissor é obrigatório'),
  senha: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Senha deve conter pelo menos uma letra minúscula, uma maiúscula e um número'),
  confirmSenha: z.string().min(6, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.senha === data.confirmSenha, {
  path: ['confirmSenha'],
  message: 'As senhas não conferem'
});

type RegisterFormData = z.infer<typeof registerSchema>;

const FALLBACK_PLAN_OPTIONS: SubscriptionPlan[] = [
  {
    id: 'STARTER',
    title: 'Starter',
    priceCents: 7990,
    subtitle: 'Para profissionais iniciando automações',
    features: [
      'Atendimento simultâneo em múltiplos números',
      'Automação de WhatsApp e e-mail para operações enxutas',
      'Setup rápido e experiência intuitiva'
    ]
  },
  {
    id: 'GROWTH',
    title: 'Growth',
    priceCents: 9490,
    subtitle: 'Ideal para equipes comerciais em expansão',
    features: [
      'Automações com maior capacidade operacional',
      'Mais velocidade para campanhas e follow-up',
      'Suporte prioritário e fluxo de crescimento'
    ]
  },
  {
    id: 'SCALE',
    title: 'Scale',
    priceCents: 12900,
    subtitle: 'Projetado para operações de alta demanda',
    features: [
      'Performance otimizada para escala rápida',
      'Fluxos avançados para times de grande volume',
      'Controle robusto e mais estabilidade operacional'
    ]
  }
];

const PLAN_TOOLTIP_COPY: Record<SubscriptionPlanId, { headline: string; items: string[] }> = {
  STARTER: {
    headline: 'Ideal para profissionais e pequenas operações.',
    items: [
      '1 conexão simultânea (1 chip/WhatsApp)',
      'Até 2.000 envios de e-mail por mês',
      'Até 3.000 envios via WhatsApp por mês',
      '100 e-mails por dia',
      '150 mensagens WhatsApp por dia',
      'Suporte padrão'
    ]
  },
  GROWTH: {
    headline: 'Para operações em crescimento.',
    items: [
      '3 conexões simultâneas (3 chips/WhatsApp)',
      'Até 5.000 envios de e-mail por mês',
      'Até 6.000 envios via WhatsApp por mês',
      '300 e-mails por dia',
      '500 mensagens WhatsApp por dia',
      'Melhor desempenho operacional',
      'Suporte prioritário'
    ]
  },
  SCALE: {
    headline: 'Para operações profissionais de alta performance.',
    items: [
      '6 conexões simultâneas (6 chips/WhatsApp)',
      'Até 10.000 envios de e-mail por mês',
      'Até 10.000 envios via WhatsApp por mês',
      'Sem limite diário de disparos',
      'Operação de alto desempenho',
      'Usuário único',
      'Suporte prioritário'
    ]
  }
};

function PlanTooltip({
  plan,
  formatCurrency,
  theme = 'dark'
}: {
  plan: SubscriptionPlan;
  formatCurrency: (amountCents: number) => string;
  theme?: 'dark' | 'light';
}) {
  const details = PLAN_TOOLTIP_COPY[plan.id];
  const containerClassName = theme === 'dark'
    ? 'pointer-events-none absolute bottom-full left-1/2 z-20 mb-4 hidden w-[320px] -translate-x-1/2 rounded-3xl border border-orange-400/30 bg-slate-950/95 p-5 text-left shadow-2xl shadow-slate-950/60 backdrop-blur-xl group-hover:block group-focus-visible:block group-focus-within:block'
    : 'pointer-events-none absolute bottom-full left-1/2 z-20 mb-3 hidden w-[320px] -translate-x-1/2 rounded-3xl border border-orange-200 bg-white p-5 text-left shadow-2xl shadow-orange-100/70 group-hover:block group-focus-visible:block group-focus-within:block';
  const titleClassName = theme === 'dark' ? 'pr-4 text-base font-semibold leading-6 text-white break-words' : 'pr-4 text-base font-semibold leading-6 text-gray-900 break-words';
  const subtitleClassName = theme === 'dark' ? 'mt-1 text-sm text-orange-200' : 'mt-1 text-sm text-orange-600';
  const itemClassName = theme === 'dark' ? 'text-sm leading-6 text-slate-200' : 'text-sm leading-6 text-gray-700';
  const bulletClassName = theme === 'dark' ? 'mt-2 h-2 w-2 rounded-full bg-orange-300' : 'mt-2 h-2 w-2 rounded-full bg-orange-400';

  return (
    <div className={containerClassName}>
      <div className="absolute bottom-0 left-1/2 h-4 w-4 -translate-x-1/2 translate-y-1/2 rotate-45 border-b border-r border-inherit bg-inherit" />
      <p className={titleClassName}>{plan.title} — R$ {formatCurrency(plan.priceCents)}/mês</p>
      <p className={subtitleClassName}>{details.headline}</p>
      <ul className="mt-4 space-y-2">
        {details.items.map((item) => (
          <li key={item} className={`flex items-start gap-3 ${itemClassName}`}>
            <span className={bulletClassName} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RegisterPage() {
  const maxInstallments = 1;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pendingData, setPendingData] = useState<RegisterFormData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [startedRegistrationId, setStartedRegistrationId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanId>('GROWTH');
  const [planOptions, setPlanOptions] = useState<SubscriptionPlan[]>(FALLBACK_PLAN_OPTIONS);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isEncryptingCard, setIsEncryptingCard] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentFormData>(createEmptyPaymentFormData);
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [lastCepLookup, setLastCepLookup] = useState('');
  const navigate = useNavigate();
  const { settings } = useGlobalSettings();
  const { createCardToken } = useMercadoPago();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      nome: '',
      email: '',
      telefone: '+55 ',
      document: '',
      registroNumero: '',
      orgaoEmissor: '',
      senha: '',
      confirmSenha: '',
    },
  });
  const selectedPlanConfig = planOptions.find((plan) => plan.id === selectedPlan) ?? planOptions[0] ?? FALLBACK_PLAN_OPTIONS[1];
  const watchedPhone = watch('telefone');
  const baseInstallmentCents = selectedPlanConfig.priceCents;
  const baseTotalCents = baseInstallmentCents;
  const appliedDiscountPercent = getCouponDiscountPercent(appliedCoupon);
  const hasDiscountPercent = appliedDiscountPercent > 0;
  const isTrialCoupon = isFreeDaysCoupon(appliedCoupon);
  const trialDays = getCouponFreeDays(appliedCoupon);
  const requiresImmediatePayment = !isTrialCoupon;
  const discountMultiplier = 1 - appliedDiscountPercent / 100;
  const getPricingForInstallments = (installmentsInput: number) => {
    const installments = Math.min(maxInstallments, Math.max(1, installmentsInput));
    const discountedTotalCents = Math.max(0, Math.round(baseTotalCents * discountMultiplier));
    const discountedInstallmentCents = discountedTotalCents;
    return {
      installments,
      discountedTotalCents,
      discountedInstallmentCents,
      oneTimeDiscountAppliedPercent: 0
    };
  };
  const selectedPricing = getPricingForInstallments(maxInstallments);
  const safeInstallments = selectedPricing.installments;
  const discountedInstallmentCents = selectedPricing.discountedInstallmentCents;
  const originalTotalCents = baseTotalCents;
  const originalInstallmentCents = Math.max(0, Math.round(originalTotalCents / safeInstallments));
  const formatCurrency = (amountCents: number) => (
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountCents / 100)
  );
  const installmentAmount = formatCurrency(discountedInstallmentCents);
  const originalInstallmentAmount = formatCurrency(originalInstallmentCents);
  const hasAnyDiscount = hasDiscountPercent;
  const cardClassName = 'rounded-[32px] border border-white/10 bg-slate-950/95 shadow-2xl shadow-slate-950/40 backdrop-blur-xl';
  const fieldClassName = 'w-full rounded-3xl border border-white/10 bg-slate-900/80 px-4 py-4 text-sm text-white placeholder:text-slate-500 focus:border-orange-400/60 focus:outline-none focus:ring-2 focus:ring-orange-400/20';
  const sectionLabelClassName = 'text-xs font-semibold uppercase tracking-[0.3em] text-slate-400';
  const primaryButtonClassName = 'w-full rounded-3xl bg-gradient-to-r from-orange-400 via-fuchsia-500 to-sky-500 px-6 py-4 text-sm font-semibold text-slate-950 shadow-xl shadow-orange-500/20 transition duration-200 hover:scale-[1.01] hover:shadow-orange-500/30 disabled:cursor-not-allowed disabled:opacity-60';
  const secondaryButtonClassName = 'rounded-3xl border border-white/10 bg-slate-900/80 px-5 py-3 text-sm font-semibold text-white transition hover:border-orange-400/40 hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60';

  useEffect(() => {
    if (!showTermsModal && !showPaymentModal) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showTermsModal, showPaymentModal]);

  useEffect(() => {
    let isActive = true;

    const loadPlans = async () => {
      try {
        const response = await fetch(buildApiUrl('/auth/register-config'));
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(payload?.message || 'Não foi possível carregar os planos');
        }

        const plans = Array.isArray(payload?.data?.plans) && payload.data.plans.length > 0
          ? payload.data.plans as SubscriptionPlan[]
          : FALLBACK_PLAN_OPTIONS;
        const defaultPlanId = (payload?.data?.defaultPlanId as SubscriptionPlanId | undefined) || plans[0]?.id || 'GROWTH';

        if (!isActive) {
          return;
        }

        setPlanOptions(plans);
        setSelectedPlan((currentPlan) => (
          plans.some((plan) => plan.id === currentPlan) ? currentPlan : defaultPlanId
        ));
      } catch (error) {
        if (isActive) {
          setPlanOptions(FALLBACK_PLAN_OPTIONS);
        }
      } finally {
        if (isActive) {
          setIsLoadingPlans(false);
        }
      }
    };

    loadPlans();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (cepDigits.length !== 8 || cepDigits === lastCepLookup) {
      return;
    }

    let isActive = true;
    const timer = setTimeout(async () => {
      setIsCepLoading(true);

      try {
        const response = await fetch(buildApiUrl(`/cep/${cepDigits}`));
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (isActive) {
            setPaymentErrors((prev) => ({
              ...prev,
              billingZip: data?.error || 'CEP não encontrado'
            }));
            setLastCepLookup(cepDigits);
          }
          return;
        }

        if (!isActive || !data) {
          return;
        }

        setPaymentData((prev) => ({
          ...prev,
          billingStreet: prev.billingStreet || data.street || '',
          billingDistrict: prev.billingDistrict || data.district || '',
          billingCity: prev.billingCity || data.city || '',
          billingState: prev.billingState || data.state || '',
          billingComplement: prev.billingComplement || data.complement || ''
        }));

        setPaymentErrors((prev) => {
          const next = { ...prev };
          delete next.billingZip;
          delete next.billingStreet;
          delete next.billingDistrict;
          delete next.billingCity;
          delete next.billingState;
          return next;
        });
        setLastCepLookup(cepDigits);
      } catch (error) {
        if (isActive) {
          setPaymentErrors((prev) => ({
            ...prev,
            billingZip: 'Erro ao consultar CEP'
          }));
          setLastCepLookup(cepDigits);
        }
      } finally {
        if (isActive) {
          setIsCepLoading(false);
        }
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [paymentData.billingZip, lastCepLookup]);

  useEffect(() => {
    const normalized = normalizePhoneInput(watchedPhone || '');
    if (normalized !== (watchedPhone || '')) {
      setValue('telefone', normalized);
    }
  }, [setValue, watchedPhone]);

  const termsText = `TERMO DE USO E CONCORDÂNCIA DO APLICATIVO LEADNEXIA

Pelo presente instrumento particular, de um lado VIA BOOTS SOLUÇÕES EM TECNOLOGIA LTDA, pessoa jurídica de direito privado, CNPJ 61.438.361/0001-09, doravante denominada LICENCIANTE, desenvolvedora e mantenedora do aplicativo LEADNEXIA, e, de outro lado, o USUÁRIO, devidamente identificado no momento do cadastro, ajustam entre si o presente TERMO DE USO, que se regerá pelas cláusulas e condições a seguir.

CLÁUSULA 1 – DO OBJETO

1.1. O presente Termo tem por objeto regular as condições de uso não exclusivo, pessoal e intransferível do aplicativo LEADNEXIA, destinado exclusivamente a oficiais de justiça, com a finalidade de auxiliar na organização, no registro e no apoio ao cumprimento de mandados judiciais, por meio de ferramentas digitais.

1.2. O LEADNEXIA constitui ferramenta de apoio, não substituindo sistemas oficiais do Poder Judiciário, nem alterando, suprimindo ou ampliando as atribuições legais do usuário.

CLÁUSULA 2 – DA NATUREZA DO USO

2.1. O presente instrumento trata-se exclusivamente de contrato de uso, não estabelecendo qualquer vínculo empregatício, funcional, associativo, societário ou institucional entre o usuário, o LEADNEXIA, a Via Boots Soluções em Tecnologia LTDA ou o Poder Judiciário.

2.2. O uso do aplicativo é realizado por conta e risco exclusivo do usuário, que permanece integralmente responsável por seus atos, registros, comunicações e pelo cumprimento das normas legais, regulamentares e administrativas aplicáveis à sua atividade.

CLÁUSULA 3 – DAS RESPONSABILIDADES DO USUÁRIO

3.1. O usuário declara, sob sua exclusiva responsabilidade, que:

I – Utilizará o aplicativo de forma ética, lícita e compatível com suas atividades;
II – É integralmente responsável pela veracidade das informações inseridas no sistema.

3.2. O usuário compromete-se a:

I – Não utilizar o aplicativo para fins ilícitos, indevidos ou contrários às normas de sua atividade;
II – Manter o sigilo sobre informações sensíveis, dados pessoais e conteúdos protegidos;
III – Zelar pela segurança de suas credenciais de acesso (login e senha), respondendo por qualquer uso indevido decorrente de sua negligência.

CLÁUSULA 4 – DAS LIMITAÇÕES DE RESPONSABILIDADE

4.1. O LEADNEXIA e a Via Boots Soluções em Tecnologia LTDA não se responsabilizam, em nenhuma hipótese:

I – Pelo uso inadequado, indevido ou em desconformidade com este Termo;
II – Por falhas decorrentes de conexão à internet, dispositivos do usuário ou serviços de terceiros.

4.2. O LEADNEXIA não se responsabiliza por regras, políticas, termos ou normas estipuladas pela empresa Meta Platforms, Inc. (Meta), incluindo, mas não se limitando, a políticas anti-spam, restrições de envio de mensagens, bloqueios, suspensões ou limitações impostas por seus serviços, tais como WhatsApp, Instagram ou Facebook.

4.3. O usuário declara estar ciente de que qualquer penalidade aplicada por terceiros não gera responsabilidade solidária ou subsidiária da Via Boots Soluções em Tecnologia LTDA.

CLÁUSULA 5 – DA VIGÊNCIA E RENOVAÇÃO

5.1. O presente Termo é celebrado por prazo determinado, conforme o plano ou modalidade de assinatura contratada pelo usuário.

5.2. O contrato será renovado automaticamente por igual período ao término de sua vigência, salvo manifestação contrária do usuário, a ser realizada com antecedência mínima de 10 (dez) dias antes do vencimento.

5.3. A manifestação contrária deverá ocorrer por meio das funcionalidades disponíveis no aplicativo ou por canal oficial indicado pela Via Boots Soluções em Tecnologia LTDA.

CLÁUSULA 6 – DA RESCISÃO, CANCELAMENTO E RESTITUIÇÃO

6.1. O presente Termo poderá ser rescindido:

I – A qualquer tempo, por iniciativa do usuário;
II – Pela Via Boots Soluções, em caso de violação das disposições deste Termo;
III – Por descontinuidade do aplicativo, mediante aviso prévio razoável.

6.2. A rescisão não gera direito a indenização de qualquer natureza, ressalvadas as hipóteses expressamente previstas neste Termo.

6.3. No caso de cancelamento do contrato de assinatura anual, será respeitado o prazo legal de arrependimento, nos termos da legislação vigente, hipótese em que será assegurada a restituição integral dos valores pagos, observadas as regras do meio de pagamento utilizado.

6.4. Decorrido o prazo legal de cancelamento, o pedido de rescisão poderá ser realizado a qualquer tempo; contudo, não haverá restituição, total ou parcial, dos valores pagos, permanecendo o acesso ao serviço ativo até o término do período contratado, salvo disposição expressa em contrário.

6.5. O usuário declara estar ciente de que a política de cancelamento ora estabelecida é compatível com a natureza do serviço prestado e com a disponibilização contínua da plataforma durante o período contratado.

CLÁUSULA 7 – DA PROTEÇÃO DE DADOS

7.1. O tratamento de dados pessoais observará integralmente a Lei Geral de Proteção de Dados – LGPD (Lei nº 13.709/2018).

7.2. Os dados coletados serão utilizados exclusivamente para:

I – Funcionamento do aplicativo;
II – Melhoria contínua dos serviços;
III – Cumprimento de obrigações legais e regulatórias.

CLÁUSULA 8 – DA PROPRIEDADE INTELECTUAL

8.1. O aplicativo LEADNEXIA, suas funcionalidades, layout, marca, código-fonte e demais elementos são de propriedade exclusiva da ViaBoots Soluções em Tecnologia LTDA, sendo vedada qualquer reprodução, modificação, engenharia reversa ou uso não autorizado.

CLÁUSULA 9 – DAS DISPOSIÇÕES GERAIS

9.1. A eventual tolerância de qualquer das partes quanto ao descumprimento de cláusulas não implicará novação ou renúncia de direitos.

9.2. Caso qualquer disposição deste Termo seja considerada inválida ou inexequível, as demais permanecerão em pleno vigor.

CLÁUSULA 10 – DO FORO

10.1. Para dirimir quaisquer controvérsias oriundas deste Termo, fica eleito o foro da comarca de domicílio da Via Boots Soluções em Tecnologia LTDA, com renúncia expressa a qualquer outro, por mais privilegiado que seja.

ACEITE


Ao clicar em “LI E CONCORDO COM OS TERMOS DE USO”, o usuário declara que leu, compreendeu e concorda integralmente com todas as cláusulas deste Termo.`;

  const submitRegistration = async (data: RegisterFormData, cardTokenId?: string) => {
    setIsSubmitting(true);
    let wasSuccessful = false;
    try {
      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('email', data.email);
      formData.append('telefone', data.telefone);
      formData.append('document', data.document);
      formData.append('senha', data.senha);
      formData.append('registroNumero', data.registroNumero);
      formData.append('orgaoEmissor', data.orgaoEmissor);
      formData.append('termsAccepted', 'true');
      formData.append('installments', String(safeInstallments));
      if (startedRegistrationId) {
        formData.append('startedRegistrationId', startedRegistrationId);
      }
      formData.append('plan', selectedPlan);
      if (cardTokenId) {
        formData.append('holderName', paymentData.holderName);
        formData.append('holderDocument', paymentData.holderDocument);
        formData.append('billingZip', paymentData.billingZip);
        formData.append('billingStreet', paymentData.billingStreet);
        formData.append('billingNumber', paymentData.billingNumber);
        formData.append('billingComplement', paymentData.billingComplement);
        formData.append('billingDistrict', paymentData.billingDistrict);
        formData.append('billingCity', paymentData.billingCity);
        formData.append('billingState', paymentData.billingState);
        formData.append('cardTokenId', cardTokenId);
      }
      if (appliedCoupon?.code) {
        formData.append('couponCode', appliedCoupon.code);
      }

      const response = await fetch(buildApiUrl('/auth/register-public'), {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro ao criar conta');
      }

      toast.success(result.message || 'Conta criada com sucesso!');
      navigate('/login');
      wasSuccessful = true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
    return wasSuccessful;
  };

  const handleApplyCoupon = async () => {
    const normalized = normalizeCouponCode(couponCode);
    if (!normalized) {
      setCouponError('Informe um cupom válido');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const response = await fetch(buildApiUrl('/coupons/validate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: normalized })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'Cupom inválido');
      }

      const parsedCoupon = parseCouponValidationResponse(data, normalized);
      setAppliedCoupon(parsedCoupon);
      setCouponCode(normalized);
      setCouponError('');

      if (isFreeDaysCoupon(parsedCoupon)) {
        setPaymentErrors({});
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao validar cupom';
      setCouponError(message);
      setAppliedCoupon(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleFormContinue = async (data: RegisterFormData) => {
    try {
      const response = await fetch(buildApiUrl('/auth/register-start'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startedRegistrationId,
          nome: data.nome,
          email: data.email,
          telefone: data.telefone,
          document: data.document,
          registroNumero: data.registroNumero,
          orgaoEmissor: data.orgaoEmissor
        })
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.message || 'Não foi possível iniciar o cadastro');
      }

      setStartedRegistrationId(payload?.data?.startedRegistrationId || null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível iniciar o cadastro');
      return;
    }

    setPendingData(data);
    setTermsAccepted(false);
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePlanContinue = () => {
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openTermsModal = () => {
    if (!pendingData) {
      return;
    }

    setTermsAccepted(false);
    setShowTermsModal(true);
  };

  const confirmTermsAndSubmit = async () => {
    if (!pendingData || !termsAccepted) {
      return;
    }

    setShowTermsModal(false);
    setShowPaymentModal(true);
  };

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const validatePayment = () => {
    if (isTrialCoupon) {
      setPaymentErrors({});
      return true;
    }

    const nextErrors: Record<string, string> = {};
    const cardDigits = paymentData.cardNumber.replace(/\D/g, '');
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (!paymentData.holderName.trim()) nextErrors.holderName = 'Nome do titular é obrigatório';
    if (!isValidCPF(paymentData.holderDocument)) nextErrors.holderDocument = 'CPF do titular inválido';
    if (cardDigits.length < 13) nextErrors.cardNumber = 'Número do cartão inválido';
    if (!/^\d{2}\/\d{2}$/.test(paymentData.cardExpiry)) nextErrors.cardExpiry = 'Validade inválida';
    if (!/^\d{3,4}$/.test(paymentData.cardCvv)) nextErrors.cardCvv = 'CVV inválido';
    if (cepDigits.length < 8) nextErrors.billingZip = 'CEP inválido';
    if (!paymentData.billingStreet.trim()) nextErrors.billingStreet = 'Endereço é obrigatório';
    if (!paymentData.billingNumber.trim()) nextErrors.billingNumber = 'Número é obrigatório';
    if (!paymentData.billingDistrict.trim()) nextErrors.billingDistrict = 'Bairro é obrigatório';
    if (!paymentData.billingCity.trim()) nextErrors.billingCity = 'Cidade é obrigatória';
    if (!/^[A-Za-z]{2}$/.test(paymentData.billingState.trim())) nextErrors.billingState = 'UF inválida';

    setPaymentErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePaymentSubmit = async () => {
    if (!pendingData) {
      return;
    }

    const skipImmediatePayment = isTrialCoupon;

    if (!skipImmediatePayment && !validatePayment()) {
      return;
    }

    let cardTokenId: string | undefined;
    if (!skipImmediatePayment) {
      try {
        setIsEncryptingCard(true);
        cardTokenId = await createCardToken(paymentData);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao tokenizar cartão';
        toast.error(message);
        return;
      } finally {
        setIsEncryptingCard(false);
      }
    }

    const wasSuccessful = await submitRegistration(pendingData, cardTokenId);
    if (!wasSuccessful) {
      return;
    }

    setShowPaymentModal(false);
    setPendingData(null);
    setStartedRegistrationId(null);
    setPaymentData(createEmptyPaymentFormData());
    setPaymentErrors({});
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  return (
    <>
      <div className="min-h-screen overflow-hidden bg-[#060C1F] text-white">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 left-1/2 h-72 w-72 rounded-full bg-[#F37335]/20 blur-3xl" />
          <div className="absolute -bottom-20 right-1/4 h-80 w-80 rounded-full bg-[#4A9EFF]/20 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.16),_transparent_20%),radial-gradient(circle_at_bottom_right,_rgba(255,105,180,0.08),_transparent_30%)]" />
        </div>

        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
          <div className="w-full max-w-7xl rounded-[32px] border border-white/10 bg-slate-950/80 shadow-2xl backdrop-blur-xl">
            <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="hidden rounded-[32px] bg-slate-900/75 p-10 lg:block">
                <div className="mb-10 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 shadow-sm">
                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  Premium SaaS Onboarding
                </div>

                <div className="space-y-8">
                  <div className="space-y-4">
                    <p className="text-sm uppercase tracking-[0.3em] text-slate-400">LeadNexia</p>
                    <h1 className="text-4xl font-semibold leading-tight tracking-tight text-white">Crie sua conta e coloque sua operação em ritmo de crescimento.</h1>
                    <p className="max-w-xl text-sm leading-7 text-slate-300">O cadastro segue o mesmo padrão da tela de acesso: ambiente premium, clareza no fluxo e foco total em conversão.</p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    {[
                      { id: 1, title: 'Conta', label: 'Dados essenciais' },
                      { id: 2, title: 'Plano', label: 'Escolha ideal' },
                      { id: 3, title: 'Ativação', label: 'Termos e pagamento' },
                    ].map((step) => {
                      const isActive = currentStep === step.id;
                      return (
                        <div
                          key={step.id}
                          className={`rounded-3xl border p-5 transition-all ${
                            isActive
                              ? 'border-orange-400/50 bg-orange-400/10 text-white shadow-lg shadow-orange-500/10'
                              : 'border-white/10 bg-slate-950/40 text-slate-300'
                          }`}
                        >
                          <p className="text-xs font-semibold uppercase tracking-[0.3em]">Passo {step.id}</p>
                          <p className="mt-3 text-lg font-semibold">{step.title}</p>
                          <p className="mt-2 text-sm text-slate-400">{step.label}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    {[
                      { title: 'Cadastro guiado', description: 'Fluxo simples para reduzir abandono e acelerar a ativação.' },
                      { title: 'Planos claros', description: 'Comparação objetiva para escolher sem fricção.' },
                      { title: 'Pagamento seguro', description: 'Tokenização de cartão e etapas separadas por contexto.' },
                      { title: 'Acesso imediato', description: 'Entrada rápida após validação do cadastro e do plano.' },
                    ].map((item) => (
                      <div key={item.title} className="rounded-3xl border border-white/10 bg-slate-950/40 p-5 shadow-lg shadow-slate-950/20 backdrop-blur-sm">
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="mt-2 text-sm text-slate-400">{item.description}</p>
                      </div>
                    ))}
                  </div>

                </div>
              </div>

              <div className="space-y-6 p-4 sm:p-8">
                <div className={cardClassName}>
                  <div className="px-6 py-6 sm:px-8">
                {currentStep === 1 && (
                  <form onSubmit={handleSubmit(handleFormContinue)} className="space-y-5">
                    <div>
                      <label htmlFor="nome" className="mb-2 block text-sm font-semibold text-slate-300">Nome completo</label>
                      <input id="nome" {...register('nome')} className={fieldClassName} placeholder="Nome completo" />
                      {errors.nome && <p className="mt-2 text-sm text-orange-300">{errors.nome.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-300">E-mail</label>
                      <input id="email" type="email" {...register('email')} className={fieldClassName} placeholder="seu@email.com" />
                      {errors.email && <p className="mt-2 text-sm text-orange-300">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="telefone" className="mb-2 block text-sm font-semibold text-slate-300">Telefone</label>
                      <input id="telefone" {...register('telefone')} className={fieldClassName} placeholder="+55 (11) 99999-9999" />
                      {errors.telefone && <p className="mt-2 text-sm text-orange-300">{errors.telefone.message}</p>}
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <label htmlFor="document" className="mb-2 block text-sm font-semibold text-slate-300">CPF</label>
                        <input
                          id="document"
                          {...register('document')}
                          className={fieldClassName}
                          placeholder="000.000.000-00"
                        />
                        {errors.document && <p className="mt-2 text-sm text-orange-300">{errors.document.message}</p>}
                      </div>

                      <div>
                        <label htmlFor="registroNumero" className="mb-2 block text-sm font-semibold text-slate-300">Número de registro</label>
                        <input
                          id="registroNumero"
                          {...register('registroNumero')}
                          className={fieldClassName}
                          placeholder="Número do registro"
                        />
                        {errors.registroNumero && <p className="mt-2 text-sm text-orange-300">{errors.registroNumero.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="orgaoEmissor" className="mb-2 block text-sm font-semibold text-slate-300">Órgão emissor</label>
                      <input id="orgaoEmissor" {...register('orgaoEmissor')} className={fieldClassName} placeholder="Órgão emissor" />
                      {errors.orgaoEmissor && <p className="mt-2 text-sm text-orange-300">{errors.orgaoEmissor.message}</p>}
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <div className="relative">
                        <label htmlFor="senha" className="mb-2 block text-sm font-semibold text-slate-300">Senha</label>
                        <input
                          id="senha"
                          type={showPassword ? 'text' : 'password'}
                          {...register('senha')}
                          className={`${fieldClassName} pr-20`}
                          placeholder="Senha segura"
                        />
                        <button
                          type="button"
                          className="absolute right-4 top-11 text-sm text-slate-400 hover:text-white"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? 'Ocultar' : 'Mostrar'}
                        </button>
                        {errors.senha && <p className="mt-2 text-sm text-orange-300">{errors.senha.message}</p>}
                      </div>

                      <div className="relative">
                        <label htmlFor="confirmSenha" className="mb-2 block text-sm font-semibold text-slate-300">Confirmar senha</label>
                        <input
                          id="confirmSenha"
                          type={showConfirmPassword ? 'text' : 'password'}
                          {...register('confirmSenha')}
                          className={`${fieldClassName} pr-20`}
                          placeholder="Repetir senha"
                        />
                        <button
                          type="button"
                          className="absolute right-4 top-11 text-sm text-slate-400 hover:text-white"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                          {showConfirmPassword ? 'Ocultar' : 'Mostrar'}
                        </button>
                        {errors.confirmSenha && <p className="mt-2 text-sm text-orange-300">{errors.confirmSenha.message}</p>}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-white/10 bg-slate-900/60 p-5">
                      <p className={sectionLabelClassName}>Segurança da conta</p>
                      <p className="mt-2 text-sm text-slate-400">Use uma senha com letras maiúsculas, minúsculas e números para manter o acesso protegido.</p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <button type="submit" className="sm:w-auto rounded-3xl bg-gradient-to-r from-orange-400 via-fuchsia-500 to-sky-500 px-6 py-4 text-sm font-semibold text-slate-950 shadow-xl shadow-orange-500/20 transition duration-200 hover:scale-[1.01]">
                        Avançar para escolher plano
                      </button>
                      <p className="text-sm text-slate-400">Já tem conta? <Link to="/login" className="font-semibold text-white transition hover:text-orange-300">Entrar</Link></p>
                    </div>
                  </form>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
                      <p className={sectionLabelClassName}>Plano sugerido</p>
                      <h3 className="mt-3 text-2xl font-bold text-white">{selectedPlanConfig.title}</h3>
                      <p className="mt-2 text-slate-400">{selectedPlanConfig.subtitle}</p>
                      <div className="mt-6 flex items-center gap-2 text-3xl font-bold text-white">
                        R$ {formatCurrency(selectedPlanConfig.priceCents)}
                        <span className="text-base font-medium text-slate-400">/mês</span>
                      </div>
                    </div>

                    <div className="grid gap-4 xl:grid-cols-3">
                      {planOptions.map((plan) => {
                        const isSelected = selectedPlan === plan.id;
                        return (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => setSelectedPlan(plan.id)}
                            className={
                              `group relative rounded-3xl border p-6 text-left transition-all ${
                                isSelected
                                  ? 'border-orange-400/60 bg-orange-400/10 shadow-lg shadow-orange-500/10'
                                  : 'border-white/10 bg-slate-900/70 hover:border-orange-400/30'
                              }`
                            }
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className="text-sm uppercase tracking-[0.3em] text-orange-300">{plan.title}</p>
                                <h3 className="mt-3 text-2xl font-bold text-white">R$ {formatCurrency(plan.priceCents)}/mês</h3>
                                <p className="mt-2 text-sm text-slate-400">{plan.subtitle}</p>
                              </div>
                              {isSelected && (
                                <span className="rounded-full bg-orange-400/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">Selecionado</span>
                              )}
                            </div>
                            <div className="mt-6 space-y-3">
                              {plan.features.map((feature) => (
                                <div key={feature} className="flex gap-3 text-sm text-slate-300">
                                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-orange-400/20 text-orange-300">✓</span>
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>
                            <PlanTooltip plan={plan} formatCurrency={formatCurrency} theme="dark" />
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className={secondaryButtonClassName}
                      >
                        Voltar ao cadastro
                      </button>
                      <button
                        type="button"
                        onClick={handlePlanContinue}
                        className="sm:w-auto rounded-3xl bg-gradient-to-r from-orange-400 via-fuchsia-500 to-sky-500 px-6 py-4 text-sm font-semibold text-slate-950 shadow-xl shadow-orange-500/20 transition duration-200 hover:scale-[1.01]"
                      >
                        Continuar para finalizar
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <p className={sectionLabelClassName}>Resumo do plano</p>
                          <h3 className="mt-2 text-2xl font-bold text-white">{selectedPlanConfig.title}</h3>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-400">Investimento mensal</p>
                          <div className="mt-2 text-3xl font-bold text-white">R$ {formatCurrency(selectedPlanConfig.priceCents)}</div>
                        </div>
                      </div>
                      <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        {selectedPlanConfig.features.map((item) => (
                          <div key={item} className="rounded-2xl bg-slate-950/70 p-4 text-sm text-slate-300 shadow-sm">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-orange-400/30 bg-orange-400/10 p-6">
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-orange-200">Atenção</p>
                      <p className="mt-2 text-sm text-orange-100">Ao continuar, você revisa os termos de uso e segue para a ativação segura da sua conta.</p>
                    </div>

                    {hasAnyDiscount && !isTrialCoupon && (
                      <div className="rounded-3xl border border-emerald-400/30 bg-emerald-400/10 p-6 text-sm text-emerald-100">
                        Cupom aplicado: {appliedCoupon?.code} com desconto de {appliedDiscountPercent}% no primeiro mês.
                      </div>
                    )}

                    {isTrialCoupon && (
                      <div className="rounded-3xl border border-sky-400/30 bg-sky-400/10 p-6 text-sm text-sky-100">
                        Cupom de teste aplicado: {trialDays} dias grátis antes do primeiro pagamento.
                      </div>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className={secondaryButtonClassName}
                      >
                        Voltar para planos
                      </button>
                      <button
                        type="button"
                        onClick={openTermsModal}
                        className="sm:w-auto rounded-3xl bg-gradient-to-r from-orange-400 via-fuchsia-500 to-sky-500 px-6 py-4 text-sm font-semibold text-slate-950 shadow-xl shadow-orange-500/20 transition duration-200 hover:scale-[1.01]"
                      >
                        Revisar termos e prosseguir
                      </button>
                    </div>
                  </div>
                )}
                  </div>
                </div>

                <aside className={`${cardClassName} p-6 sm:p-8`}>
                  <div className="space-y-6">
                    <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
                      <p className={sectionLabelClassName}>Acesso existente</p>
                      <p className="mt-2 text-sm text-slate-400">Se você já criou sua conta, entre diretamente na plataforma.</p>
                      <Link to="/login" className="mt-4 inline-flex items-center text-sm font-semibold text-white transition hover:text-orange-300">
                        Ir para o login →
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showTermsModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-6 sm:p-8 backdrop-blur-sm">
        <div className="w-full max-w-3xl overflow-hidden rounded-[32px] border border-white/10 bg-slate-950 shadow-2xl shadow-slate-950/50">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 text-sm">
            <h3 className="text-lg font-semibold text-white">Termo de uso</h3>
            <button
              type="button"
              onClick={() => setShowTermsModal(false)}
              className="text-slate-400 transition-colors hover:text-white"
              disabled={isSubmitting}
            >
              Fechar
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto px-6 py-4 text-sm whitespace-pre-line text-slate-300">
            {termsText}
          </div>
          <div className="border-t border-white/10 px-6 py-4">
            <label className="flex items-start gap-3 text-sm text-slate-200">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-900 text-orange-400 focus:ring-2 focus:ring-orange-400/20"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>LI E CONCORDO COM OS TERMOS DE USO</span>
            </label>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className={secondaryButtonClassName}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmTermsAndSubmit}
                disabled={!termsAccepted || isSubmitting}
                className={primaryButtonClassName}
              >
                {isTrialCoupon ? 'Continuar' : 'Continuar para pagamento'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
      {showPaymentModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
        <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] border border-white/10 bg-slate-950 shadow-2xl shadow-slate-950/50">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {requiresImmediatePayment ? 'Pagamento' : 'Ativar dias grátis'}
              </h3>
              <p className="text-sm text-slate-400">
                {requiresImmediatePayment
                  ? 'Finalize sua assinatura com cartão de crédito.'
                  : `Seu cupom libera ${trialDays} dias grátis. O pagamento será solicitado depois.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPaymentModal(false)}
              className="text-slate-400 transition-colors hover:text-white"
              disabled={isSubmitting}
            >
              Fechar
            </button>
          </div>
          <div className="px-6 py-6 flex-1 overflow-y-auto text-sm">
            <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-5">
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Dados do titular</h4>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome no cartão</label>
                  <input
                    type="text"
                    value={paymentData.holderName}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, holderName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.holderName && (
                    <p className="mt-2 text-sm text-red-600">{paymentErrors.holderName}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CPF do titular</label>
                    <input
                      type="text"
                      value={paymentData.holderDocument}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, holderDocument: maskCPF(e.target.value) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.holderDocument && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.holderDocument}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número do cartão</label>
                    <input
                      type="text"
                      value={paymentData.cardNumber}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardNumber && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardNumber}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validade</label>
                    <input
                      type="text"
                      value={paymentData.cardExpiry}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, cardExpiry: formatExpiry(e.target.value) }))}
                      placeholder="MM/AA"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardExpiry && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardExpiry}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                    <input
                      type="password"
                      value={paymentData.cardCvv}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, cardCvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardCvv && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardCvv}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cobrança</label>
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                      Mensal recorrente
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Endereço de cobrança</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CEP
                      {isCepLoading && (
                        <span className="ml-2 text-xs font-medium text-gray-400">Buscando...</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={paymentData.billingZip}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingZip: formatCep(e.target.value) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingZip && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingZip}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
                    <input
                      type="text"
                      value={paymentData.billingStreet}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingStreet: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingStreet && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingStreet}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                    <input
                      type="text"
                      value={paymentData.billingNumber}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingNumber: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingNumber && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingNumber}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                    <input
                      type="text"
                      value={paymentData.billingComplement}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingComplement: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                    <input
                      type="text"
                      value={paymentData.billingDistrict}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingDistrict: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingDistrict && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingDistrict}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cidade</label>
                    <input
                      type="text"
                      value={paymentData.billingCity}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingCity: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingCity && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingCity}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">UF</label>
                    <input
                      type="text"
                      value={paymentData.billingState}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingState: e.target.value.toUpperCase().slice(0, 2) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingState && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingState}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
                <p className="text-sm font-semibold text-gray-700">
                  Escolha seu plano
                  {isLoadingPlans ? ' carregando...' : ''}
                </p>
                <div className="space-y-3">
                  {planOptions.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`group relative w-full rounded-2xl border p-4 text-left transition-all ${selectedPlan === plan.id ? 'border-orange-400 bg-orange-50 shadow-sm' : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{plan.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{plan.subtitle}</p>
                        </div>
                        <span className={`text-lg font-bold ${selectedPlan === plan.id ? 'text-orange-600' : 'text-gray-900'}`}>
                          R$ {formatCurrency(plan.priceCents)}/mês
                        </span>
                      </div>
                      <div className="mt-3 hidden rounded-2xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 group-hover:block">
                        <p className="font-semibold text-gray-800 mb-2">Vantagens</p>
                        <ul className="space-y-1">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2">
                              <span className="mt-1 h-2 w-2 rounded-full bg-orange-400 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <PlanTooltip plan={plan} formatCurrency={formatCurrency} theme="light" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 space-y-4">
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Plano mensal LeadNexia</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    R$ {installmentAmount}/mês
                  </p>
                  {hasAnyDiscount && (
                    <p className="text-xs text-gray-400 line-through">
                      R$ {originalInstallmentAmount}/mês
                    </p>
                  )}
                  <p className="text-sm text-gray-500">Cobrança recorrente mensal</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Cupom de desconto</label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        const nextValue = normalizeCouponCode(e.target.value);
                        setCouponCode(nextValue);
                        setCouponError('');
                        if (appliedCoupon && appliedCoupon.code !== nextValue) {
                          setAppliedCoupon(null);
                        }
                      }}
                      placeholder="Insira o cupom"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting || isApplyingCoupon}
                    />
                    <button
                      type="button"
                      onClick={appliedCoupon ? handleRemoveCoupon : handleApplyCoupon}
                      disabled={isSubmitting || isApplyingCoupon || (!couponCode.trim() && !appliedCoupon)}
                      className="rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}
                    >
                      {appliedCoupon ? 'Remover' : (isApplyingCoupon ? 'Validando...' : 'Aplicar')}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-sm text-red-600">{couponError}</p>
                  )}
                  {appliedCoupon && (
                    <p className="text-sm text-emerald-700">
                      {getCouponSummaryText(appliedCoupon)}
                    </p>
                  )}
                  {isTrialCoupon && (
                    <p className="text-xs text-emerald-700">
                      Você terá acesso imediato por {trialDays} dias. Depois disso, será necessário pagar.
                    </p>
                  )}
                </div>
                {requiresImmediatePayment ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <strong>Importante:</strong> seu cadastro será ativado após a confirmação do pagamento.
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                    <strong>Dias grátis ativos:</strong> seu acesso será liberado imediatamente por {trialDays} dias.
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  {requiresImmediatePayment
                    ? 'Ao continuar, você autoriza a cobrança recorrente mensal no cartão informado.'
                    : 'Quando os dias grátis terminarem, você verá uma tela interna para realizar o pagamento.'}
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowTermsModal(true);
                }}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handlePaymentSubmit}
                disabled={isSubmitting || isEncryptingCard}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)'
                }}
              >
                {isEncryptingCard
                  ? 'Tokenizando cartão...'
                  : isSubmitting
                    ? 'Processando...'
                    : isTrialCoupon
                      ? `Concluir cadastro com ${trialDays} dias grátis`
                      : 'Confirmar pagamento e criar conta'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );
}
